import { useState } from 'react';
import { FileText, ImageIcon, Loader2, Paperclip } from 'lucide-react';
import { toast } from '@/store/toastStore';
import {
  openKbAttachment,
  type KbDocumentAttachment,
} from '@/services/knowledge-base.service';

export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(size >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * One attachment row: icon, clickable filename, size.
 *
 * The filename is a button, not an `<a href>`. KB objects live under a private
 * S3 prefix, so the `url` on the row is unreachable from the browser — the
 * click has to ask the API for a signed URL first, which needs the bearer
 * token. Shared by all three attachment surfaces so that stays true in one
 * place; `trailing` carries whatever each surface adds after the size (the
 * editor puts its remove button there).
 */
export function KbAttachmentRow({
  attachment,
  trailing,
}: {
  attachment: KbDocumentAttachment;
  trailing?: React.ReactNode;
}) {
  const [isOpening, setIsOpening] = useState(false);

  async function handleOpen() {
    setIsOpening(true);
    try {
      await openKbAttachment(attachment.kbDocumentAttachmentId);
    } catch (error) {
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Could not open this file.';
      toast.error('Attachment unavailable', message);
    } finally {
      setIsOpening(false);
    }
  }

  return (
    <li className="flex items-center gap-3 py-2">
      {isOpening ? (
        <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-slate-400" />
      ) : attachment.kind === 'image' ? (
        <ImageIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
      ) : (
        <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" />
      )}
      <button
        type="button"
        onClick={handleOpen}
        disabled={isOpening}
        className="min-w-0 flex-1 truncate text-left text-sm text-blue-600 hover:underline disabled:opacity-60"
        title={attachment.originalFilename}
      >
        {attachment.originalFilename}
      </button>
      <span className="flex-shrink-0 text-xs text-slate-500">
        {formatBytes(attachment.sizeBytes)}
      </span>
      {trailing}
    </li>
  );
}

/** Read-only attachments card, shared by the admin viewer and the staff article page. */
export function KbAttachmentsCard({ attachments }: { attachments: KbDocumentAttachment[] }) {
  const visible = attachments.filter((a) => !a.isInline);
  if (visible.length === 0) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-2 flex items-center gap-2">
        <Paperclip className="h-4 w-4 text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-900">Attachments</h3>
        <span className="text-xs text-slate-500">{visible.length}</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {visible.map((att) => (
          <KbAttachmentRow key={att.kbDocumentAttachmentId} attachment={att} />
        ))}
      </ul>
    </div>
  );
}
